import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProviderImage from '../components/ProviderImage';
import { useAppState } from '../state/AppState';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { formatSessionDate, formatSessionTime } from '../utils/dateUtils';

/**
 * ScreenT - Appointment confirmation screen.
 *
 * Confirms the user's appointment and displays session details.
 */
const ScreenT: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();

  useEffect(() => {
    // Clear reservation timer on mount
    if (state.reservationStartTime) {
      dispatch({ type: 'SET_RESERVATION_START_TIME', payload: null });
    }
  }, [dispatch, state.reservationStartTime]);


  // Use provider name from availability data, fallback to preferred provider
  const providerName = getProviderNameFromAvailability(state.availabilities, null);
  const sessionDate = formatSessionDate(state.appointmentDate);
  const sessionTime = formatSessionTime(state.appointmentTime);
  const sessionLocation = state.location 
    ? (state.locationAddress ? `${state.location}\n${state.locationAddress}` : state.location)
    : '1234 Mockup Street\nSuite 202\nSeattle, WA 98122';

  // Get location data from availableLocations
  const getLocationData = () => {
    if (!state.locationCode || !state.availableLocations) {
      return null;
    }
    
    return state.availableLocations.find(loc => loc.id === state.locationCode) || null;
  };

  // Check if location has map data from availableLocations
  const hasLocationMap = (): boolean => {
    const location = getLocationData();
    // Location has map data if it has lat and lng (which indicates map.address exists)
    return !!(location && location.lat && location.lng);
  };

  // Get location data for map address and parking access
  const locationData = getLocationData();
  const mapAddress = locationData ? (locationData as any).mapAddress : null;
  const parkingAccess = locationData ? (locationData as any).parkingAccess : null;
  const directionsLink = locationData ? (locationData as any).link : null;

  // Generate and download .ics file
  const handleAddToCalendar = () => {
    if (!state.appointmentDate || !state.appointmentTime) {
      return;
    }

    // Parse appointment date (format: "2025-10-21" or "2025-10-21T15:00:00")
    const datePart = state.appointmentDate.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);

    // Parse appointment time (format: "12:00 PM" or "12pm")
    const timeStr = state.appointmentTime.toLowerCase().replace(/\s+/g, '');
    const timeMatch = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/);
    
    if (!timeMatch) {
      return;
    }

    const hour = parseInt(timeMatch[1]);
    const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const period = timeMatch[3];

    // Convert to 24-hour format
    let hour24 = hour;
    if (period === 'pm' && hour !== 12) {
      hour24 += 12;
    } else if (period === 'am' && hour === 12) {
      hour24 = 0;
    }

    // Create start and end dates (appointment is 1 hour)
    const startDate = new Date(year, month - 1, day, hour24, minute, 0);
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 1);

    // Format dates for .ics (local time format: YYYYMMDDTHHMMSS)
    const formatICSDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const startDateStr = formatICSDate(startDate);
    const endDateStr = formatICSDate(endDate);
    
    // DTSTAMP should be in UTC format
    const formatICSDateUTC = (date: Date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };
    const nowDateStr = formatICSDateUTC(new Date());

    // Escape special characters in .ics format
    const escapeICS = (text: string) => {
      return text.replace(/\\/g, '\\\\')
                 .replace(/;/g, '\\;')
                 .replace(/,/g, '\\,')
                 .replace(/\n/g, '\\n');
    };

    // Generate .ics content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Mindful Therapy Group//Appointment Booking//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@mindfultherapygroup.com`,
      `DTSTAMP:${nowDateStr}`,
      `DTSTART:${startDateStr}`,
      `DTEND:${endDateStr}`,
      `SUMMARY:${escapeICS(`Appointment with ${providerName}`)}`,
      `DESCRIPTION:${escapeICS(`Appointment scheduled with ${providerName}\n\nDate: ${sessionDate}\nTime: ${sessionTime}\nLocation: ${sessionLocation.replace(/\n/g, ', ')}`)}`,
      `LOCATION:${escapeICS(sessionLocation.replace(/\n/g, ', '))}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `appointment-${providerName.replace(/\s+/g, '-')}-${datePart}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main">
          <div style={{ maxWidth: 420, margin: '0 auto', marginBottom: 18, textAlign: 'left' }}>
            <ProviderImage
              maxWidth={480}
              maxHeight={480}
              borderRadius={24}
            />
            <div className="ml-16" style={{ color: '#185F93', fontWeight: 700, fontSize: '1.02rem', margin: '18px 0 6px 0' }}>Thank You!</div>
            <h2 className="ml-16" style={{ color: '#185F93', fontWeight: 800, fontSize: '2.4rem', margin: '0 0 8px 0', lineHeight: 1.1 }}>
              Your visit with<br />{providerName} has<br />been scheduled.
            </h2>
            <div style={{ background: '#fff', borderRadius: 18, padding: '18px 18px 18px 18px', margin: '18px 0 18px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div className="appointment-section-title">When</div>
              <div className="text-soft" style={{ fontWeight: 200, fontSize: '1rem', marginBottom: 2 }}>{sessionDate}</div>
              <div className="text-soft" style={{ fontWeight: 200, fontSize: '1rem', marginBottom: 12 }}>{sessionTime}</div>
              <hr style={{ border: 'none', borderTop: '3px solid #e69e6d', margin: '0 0 12px 0', width: '100%' }} />
              <div className="appointment-section-title">Where</div>
              <div className="text-soft" style={{ fontWeight: 200, fontSize: '1rem', whiteSpace: 'pre-line' }}>{sessionLocation}</div>
              {hasLocationMap() && (
                <>
                  <hr className="horizontal-line mt-18 mb-18" style={{ borderTop: '2px solid #999999' }}/>
                  {mapAddress && (
                    <div className="mb-2 text-soft" style={{ fontWeight: 200, fontSize: '1rem' }}>
                      {directionsLink ? (
                        <a 
                          href={directionsLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline', color: 'inherit' }}
                        >
                          {mapAddress}
                        </a>
                      ) : (
                        mapAddress
                      )}
                    </div>
                  )}
                  {parkingAccess && parkingAccess.trim() !== '' && (
                    <div className="mb-18 text-soft" style={{ fontWeight: 200, fontSize: '1rem' }}>
                      <a 
                        href={parkingAccess} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline', color: 'inherit' }}
                      >
                        Parking Access Information
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ background: '#fff', padding: '18px 18px 18px 18px', margin: '18px 0 18px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ color: '#888', fontSize: '0.92rem', margin: '0 0 24px 0', lineHeight: 1.4, textAlign: 'left' }}>
                We will reserve your selected timeslot with this provider on a recurring basis to ensure consistent and uninterrupted service. Please coordinate with your provider to schedule any necessary follow-up appointments. This will help maintain a seamless and efficient process for your ongoing needs.
              </div>
              <div className="button-container">
                <button
                  className="pill-btn-standard mb-16"
                  onClick={() => {
                    if (state.licenseKey) {
                      window.open(`https://pp-wfe-100.advancedmd.com/${state.licenseKey}/account/logon`, '_blank');
                    } else {
                      navigate('/p');
                    }
                  }}
                  aria-label="Complete your forms"
                >
                  Complete Your Forms
                </button>
                <button
                  className="pill-btn-standard"
                  onClick={handleAddToCalendar}
                  aria-label="Add to calendar"
                >
                  Add To Calendar
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ScreenT; 